import {
  AfterViewInit, ChangeDetectorRef,
  Component,
  EventEmitter, Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {GameComponent, HymnWithStatus} from "../game/game.component";
import {MatIconModule} from "@angular/material/icon";
import {NgForOf, NgIf, NgTemplateOutlet} from "@angular/common";
import {MatButtonModule} from "@angular/material/button";
import {Hymn} from "../../hymn/hymn";
import {LayoutService} from "../../service/layout.service";
import {SidenavService} from "../../service/sidenav.service";
import {ThemeService} from "../../service/theme.service";
import {MidiPlayerService} from "../../service/midi-player.service";
import {HymnSelectionService} from "../../service/hymn-selection.service";
import {SettingsService} from "../../service/settings.service";
import {BackButtonService} from "../../service/back-button.service";
import {ConfettiService} from "../../service/confetti.service";
import {HymnListComponent} from "../modal/hymn-list/hymn-list.component";
import {MatDialog} from "@angular/material/dialog";
import {translation} from "../../translation";

@Component({
  selector: 'app-two-player',
  standalone: true,
  imports: [
    GameComponent,
    MatIconModule,
    NgForOf,
    MatButtonModule,
    NgIf,
    NgTemplateOutlet
  ],
  templateUrl: './two-player.component.html',
  styleUrl: './two-player.component.scss'
})
export class TwoPlayerComponent implements OnInit, OnDestroy {

  @Output() exit = new EventEmitter<void>();

  @ViewChild('player1') player1!: GameComponent;
  @ViewChild('player2') player2!: GameComponent;

  @Input() favorites: boolean = false;

  playerLayoutOpposite = true;

  totalRounds = 5;
  round = -1;
  hymns: Hymn[] = [];
  playButtonActive = false;

  player1Selected = false;
  player2Selected = false;
  player1Answer?: Hymn;
  player2Answer?: Hymn;
  fastestPlayer?: number;

  player1Correct = false;
  player2Correct = false;

  player1score = 0;
  player2score = 0;

  player1stars = 0;
  player2stars = 0;

  options: HymnWithStatus[] = [];
  winningOption?: Hymn;
  startTime = 0;
  countdown = 3;

  loading = true;

  timeouts: any = [];

  player1Message = '';
  player2Message = '';

  active = true;

  // private resizeSubject = new Subject<Event>();
  // private resizeSubscription!: Subscription;

  constructor(private cdr: ChangeDetectorRef,
              private layoutService: LayoutService, private sidenavService: SidenavService,
              private themeService: ThemeService, protected midiPlayerService: MidiPlayerService,
              private hymnSelectionService: HymnSelectionService, private settingsService: SettingsService,
              private backButtonService: BackButtonService, private confettiService: ConfettiService,
              private dialog: MatDialog) {
    this.midiPlayerService.status$.subscribe(status => {
      if (status === 'playing') {
        this.playButtonActive = false;
      } else if (!this.player1Selected || !this.player2Selected) {
        this.playButtonActive = true;
      }
    });
  }

  ngOnDestroy(): void {
    this.active = false;
    this.layoutService.unlockOrientation();
  }

  ngOnInit(): void {
    this.active = true;
    this.changeLayout();

    // this.resizeSubscription = this.resizeSubject.pipe(
    //   debounceTime(500)
    // ).subscribe(() => {
    //   this.changeLayout();
    // });

    this.initialize(translation.guess_the_hymn.two_player_instruction.replace("{x}", `${this.totalRounds}`))
  }

  changeLayout() {
    const originalPlayerLayoutOpposite = this.playerLayoutOpposite; // Store original state

    if (window.innerWidth > window.innerHeight) {
      if(this.playerLayoutOpposite) {
        this.playerLayoutOpposite = false;
        this.layoutService.lockOrientation("landscape");
      }
    } else {
      if(!this.playerLayoutOpposite) {
        this.playerLayoutOpposite = true;
        this.layoutService.lockOrientation("portrait");
      }
    }

    // Only trigger change detection if the value actually changed
    if (originalPlayerLayoutOpposite !== this.playerLayoutOpposite) {
      this.cdr.detectChanges(); // Force change detection for this component and its children
      // OR if you just need to mark for check in the next cycle (more efficient for OnPush)
      // this.cdr.markForCheck();
    }
  }

  // @HostListener('window:resize', ['$event'])
  // onResize(event: Event) {
  //   this.resizeSubject.next(event);
  // }

  async resetState() {
    this.hymns = this.hymnSelectionService.getRandomList(this.favorites);
    this.player1score = 0;
    this.player2score = 0;
    this.player1stars = 0;
    this.player2stars = 0;
    this.round = -1;
    this.countdown = 3;
    this.fastestPlayer = undefined;
    this.player1Answer = undefined;
    this.player2Answer = undefined;
    this.player1Correct = false;
    this.player2Correct = false;
  }

  async initialize(message: string) {
    this.player1Message = message;
    this.player2Message = message;
    this.resetState();
    this.backButtonService.registerBackButtonAction(() => {
      this.onExit();
    });
  }

  async begin() {
    this.resetState();
    this.midiPlayerService.initAudioContext();
    this.nextRound(false);
  }

  async nextRound(wait: boolean = true) {
    this.round++;
    this.countdown = 3;
    this.player1Selected = false;
    this.player2Selected = false;
    this.fastestPlayer = undefined;
    this.player1Answer = undefined;
    this.player2Answer = undefined;
    this.player1Correct = false;
    this.player2Correct = false;

    this.loading = true;
    if(wait) {
      await this.performCountdown();
    }

    if(this.round >= this.hymns.length) {
      this.hymns = this.hymnSelectionService.getRandomList(false);
    }

    this.winningOption = this.hymns[this.round];
    this.options = this.generateOptions(this.winningOption).map(h => ({...h, status: 'none'}));


    if(this.winningOption?.hasChorus && this.winningOption.timestamps?.chorus
      && Math.floor(Math.random() * 2) == 0) { // 50% chance to play chorus
      this.startTime = this.winningOption.timestamps.chorus;
    } else {
      this.startTime = 0;
    }

    const player1Options = this.options.map(o => ({...o, status: 'none'}));

    // Ensure a different order for player 2 to avoid players seeing each other's choices
    const player2Options = this.hymnSelectionService.shuffleArray(this.options.map(o => ({...o, status: 'none'})));

    this.player1Message = translation.guess_the_hymn.which_hymn;
    this.player2Message = translation.guess_the_hymn.which_hymn;

    this.player1.nextRound(player1Options, this.winningOption, this.startTime);
    await this.player2.nextRound(player2Options, this.winningOption, this.startTime);

    this.loading = false;

    if(this.active) {
      if (!this.midiPlayerService.hasAudioContext()) {
        this.playButtonActive = true;
      } else {
        this.playButtonActive = false;
        this.playMidi();
      }
    }
  }

  generateOptions(winningOption: Hymn, extraOptions = 3): Hymn[] {
    const options = [winningOption];
    while (options.length <= extraOptions) {
      const randomIndex = Math.floor(Math.random() * this.hymns.length);
      const randomHymn = this.hymns[randomIndex];
      // check that the current options don't contain this option
      if (!options.some(o => o.hymnNumber === randomHymn.hymnNumber)) {
        options.push(randomHymn);
      }
    }
    return this.hymnSelectionService.shuffleArray(options);
  }

  async performCountdown() {
    for (let i = this.countdown; i > 0; i--) {
      await new Promise(resolve => {
        this.timeouts.push(setTimeout(resolve, 1000));
      });
      this.countdown--;
    }
  }

  playMidi(startTime: number = this.startTime) {
    this.midiPlayerService.playHymnPieceForGame(this.winningOption!, 100, false, this.startTime);
  }

  onPlayerOptionSelected(player: number, chosenOption: Hymn) {

    if (player === 1) {
      this.player1Selected = true;
      this.player1Answer = chosenOption;
      this.player1Correct = chosenOption.hymnNumber === this.winningOption?.hymnNumber;
    } else {
      this.player2Selected = true;
      this.player2Answer = chosenOption;
      this.player2Correct = chosenOption.hymnNumber === this.winningOption?.hymnNumber;
    }

    if(this.fastestPlayer == undefined) {
      console.log('Player', player, 'is the fastest');
      this.fastestPlayer = player;
    }

    if(this.player1Selected && this.player2Selected) {
      this.decideRoundWinner();

      if(this.player1score >= this.totalRounds || this.player2score >= this.totalRounds) {
        this.player1.setDisableOptions(true);
        this.player1.showPlayer();
        this.player2.setDisableOptions(true);
        this.player2.showPlayer();

        setTimeout(() => {
          this.decideFinalWinner();
        }, 2000);
      } else {
        // Only stop the midi at the end of the round, not when a player has won
        this.midiPlayerService.stop();

        this.player1.setDisableOptions(true);
        this.player1.showPlayer();
        this.player2.setDisableOptions(true);
        this.player2.showPlayer();

        this.midiPlayerService.initAudioContext();
        this.nextRound();
      }
    }
  }

  private decideFinalWinner() {
    if(this.player1score > this.player2score) {
      this.player1.win(translation.guess_the_hymn.you_win, this.playerLayoutOpposite);
      this.player1.showPointsAward("🥇");
      this.player2.lose(translation.guess_the_hymn.you_lose);
    } else if(this.player1score < this.player2score) {
      this.player1.lose(translation.guess_the_hymn.you_lose);
      this.player2.win(translation.guess_the_hymn.you_win, this.playerLayoutOpposite);
      this.player2.showPointsAward("🥇");
    } else {
      this.player1.draw(translation.guess_the_hymn.draw);
      this.player1.showPointsAward("🤝");
      this.player2.draw(translation.guess_the_hymn.draw);
      this.player2.showPointsAward("🤝");
    }
  }

  private decideRoundWinner() {
    console.log(`Fastest player: ${this.fastestPlayer}`);
    console.log('Player 1 is fastest:', this.fastestPlayer == 1);

    const players = this.fastestPlayer == 1 ? [this.player1, this.player2] : [this.player2, this.player1];
    const results = this.fastestPlayer == 1 ? [this.player1Correct, this.player2Correct] : [this.player2Correct, this.player1Correct];

    console.log('Players:', players.map(p => p.getPlayerNumber()));
    console.log('Results:', results);

    let resultMessage = [];
    if(results[0]) {
      resultMessage.push(translation.guess_the_hymn.correct);
      if(players[0].getPlayerNumber() == 1) {
        this.player1score += 1;
        this.player1.showPointsAward("⭐");
      } else {
        this.player2score += 1;
        this.player2.showPointsAward("⭐");
      }

      if(results[1]) {
        resultMessage.push(translation.guess_the_hymn.too_slow);
        if(players[1].getPlayerNumber() == 1) {
          this.player1score += 0.5;
          this.player1.showPointsAward("🐢");
        } else {
          this.player2score += 0.5;
          this.player2.showPointsAward("🐢");
        }
      } else {
        resultMessage.push(translation.guess_the_hymn.incorrect);
      }
    } else {
      resultMessage.push(translation.guess_the_hymn.incorrect);

      if(results[1]) {
        resultMessage.push(translation.guess_the_hymn.correct);
        if(players[1].getPlayerNumber() == 1) {
          this.player1score += 1;
        } else {
          this.player2score += 1;
        }

      } else {
        resultMessage.push(translation.guess_the_hymn.incorrect);
      }
    }

    this.player1stars = Math.min(this.player1score, this.totalRounds + 0.5);
    this.player2stars = Math.min(this.player2score, this.totalRounds + 0.5);

    console.log('Result message:', resultMessage);

    players.forEach((player, index) => {
      console.log(`Player ${player.getPlayerNumber()} message:`, resultMessage[index]);

      if(player.getPlayerNumber() == 1) {
        this.player1Message = resultMessage[index];
      } else {
        this.player2Message = resultMessage[index];
      }
    });

    // Fastest player gets dark background if they are incorrect
    players[0].setDarkBackground(!results[0]);
    // Slowest player gets dark background if fastest player is correct or if they are incorrect
    players[1].setDarkBackground(results[0] || !results[1]);
  }

  protected readonly Math = Math;

  stopMidi() {
    this.midiPlayerService.stop();
  }

  onExit() {
    this.timeouts.forEach((timeout: any) => {
      clearTimeout(timeout);
    });
    this.stopMidi();
    this.confettiService.stop();
    this.exit.emit();
  }

  onRestart() {
    this.stopMidi();
    this.begin();
  }

  calculateEmptyStars(player2stars: number) {
    const emptyStars = Math.floor(this.totalRounds - player2stars);
    if(emptyStars < 0) {
      return 0;
    } else if(emptyStars > this.totalRounds) {
      return this.totalRounds;
    } else {
      return emptyStars;
    }
  }

  toggleLayout() {
    this.playerLayoutOpposite = !this.playerLayoutOpposite;
    if(this.playerLayoutOpposite) {
      this.layoutService.lockOrientation("portrait").then(() =>
        this.cdr.detectChanges()
      );
    } else {
      this.layoutService.lockOrientation("landscape").then(() =>
        this.cdr.detectChanges()
      );
    }
  }

  openInfoDialog() {
    this.dialog.closeAll();
    const dialogRef = this.dialog.open(
      HymnListComponent,
      {data: this.hymns.slice(0, this.round+1)}
    );
    this.active = true;
    dialogRef.afterClosed().subscribe(() => {
      this.active = true;
    })
  }
}
